/**
 * API client for communicating with the Flask backend
 */

import type {
  SpotifyProfile,
  Playlist,
  Track,
  GeneratedPlaylist,
  PlaylistValidation,
  ApiError,
  PlaylistOwnerResponse,
  PlaylistSearchResponse,
  TrackResolveResponse,
  TrackSearchResponse,
} from '../types';

const API_BASE = '/api';

class ApiClient {
  private async fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${API_BASE}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      const error = data as ApiError;
      // Include retry_after in error message if it's a rate limit error
      if (response.status === 429 && 'retry_after' in data) {
        const retryAfter = (data as any).retry_after;
        throw new Error(`${error.message || error.error || 'Request failed'}|||${retryAfter}`);
      }
      throw new Error(error.message || error.error || 'Request failed');
    }

    return data;
  }

  /**
   * Fetch a user's public profile
   */
  async getProfile(username: string): Promise<SpotifyProfile> {
    return this.fetchJson<SpotifyProfile>(`/profile/${encodeURIComponent(username)}`);
  }

  /**
   * Fetch a user's public playlists
   */
  async getPlaylists(username: string): Promise<{ playlists: Playlist[] }> {
    return this.fetchJson<{ playlists: Playlist[] }>(`/profile/${encodeURIComponent(username)}/playlists`);
  }

  /**
   * Get tracks from a playlist
   */
  async getPlaylistTracks(playlistId: string): Promise<{ name: string; tracks: Track[] }> {
    return this.fetchJson<{ name: string; tracks: Track[] }>(`/playlist/${playlistId}/tracks`);
  }

  /**
   * Validate a playlist URL
   */
  async validatePlaylistUrl(playlistUrl: string): Promise<PlaylistValidation> {
    return this.fetchJson<PlaylistValidation>('/playlist/validate', {
      method: 'POST',
      body: JSON.stringify({ playlist_url: playlistUrl }),
    });
  }

  /**
   * Generate a playlist from an existing playlist
   */
  async generateFromPlaylist(playlistId: string): Promise<GeneratedPlaylist> {
    return this.fetchJson<GeneratedPlaylist>('/generate/from-playlist', {
      method: 'POST',
      body: JSON.stringify({ playlist_id: playlistId }),
    });
  }

  /**
   * Generate a playlist from a text description
   */
  async generateFromText(description: string): Promise<GeneratedPlaylist> {
    return this.fetchJson<GeneratedPlaylist>('/generate/from-text', {
      method: 'POST',
      body: JSON.stringify({ description }),
    });
  }

  /**
   * Get playlist owner username from playlist URL
   */
  async getPlaylistOwner(playlistUrl: string): Promise<PlaylistOwnerResponse> {
    return this.fetchJson<PlaylistOwnerResponse>('/playlist/owner', {
      method: 'POST',
      body: JSON.stringify({ playlist_url: playlistUrl }),
    });
  }

  async resolveTracks(text: string): Promise<TrackResolveResponse> {
    return this.fetchJson<TrackResolveResponse>('/tracks/resolve', {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  }

  async generateFromTracks(trackIds: string[]): Promise<GeneratedPlaylist> {
    return this.fetchJson<GeneratedPlaylist>('/generate/from-tracks', {
      method: 'POST',
      body: JSON.stringify({ track_ids: trackIds }),
    });
  }

  async searchTracks(query: string, limit: number = 10): Promise<TrackSearchResponse> {
    return this.fetchJson<TrackSearchResponse>(
      `/search/tracks?q=${encodeURIComponent(query)}&limit=${limit}`
    );
  }

  /**
   * Search for public playlists
   */
  async searchPlaylists(query: string, limit: number = 10): Promise<PlaylistSearchResponse> {
    return this.fetchJson<PlaylistSearchResponse>(
      `/search/playlists?q=${encodeURIComponent(query)}&limit=${limit}`
    );
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string }> {
    return this.fetchJson<{ status: string }>('/health');
  }

  /**
   * Test rate limiting without calling Logic API
   */
  async testRateLimit(): Promise<GeneratedPlaylist> {
    return this.fetchJson<GeneratedPlaylist>('/generate/test-rate-limit', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }
}

export const api = new ApiClient();
